import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './ImportPage.module.css';
import routes from 'routes';
import { ReactComponent as ChevronLeftIcon } from 'assets/images/icon_chevron_left.svg';

import MetaTags from 'components/common/MetaTags';
import MainContent from 'components/NewDesign/MainContent';
import PageHeader from 'components/NewDesign/PageHeader';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm/CustomNewConfirm';
import PinProtectedAction from 'components/common/PinProtected/PinProtectedAction';
import FileUpload from 'components/admin/FileUpload';
import ImportProgress from 'components/admin/ImportProgress';
import ImportErrors from 'components/admin/ImportErrors';
import ImportPreview from 'components/admin/ImportPreview';
import BulkOperationResultModal from 'components/admin/BulkOperationResultModal';
import { sendClubInvitationsBulk } from 'api/userApi';

import {
  getImportConfig,
  getImportTypeFromPath,
  IMPORT_TYPES,
  getApiValue,
} from 'config/admin/importTypes';
import { groupingBasisOf } from 'utils/groupLabel';
import { clubFeaturesOf } from 'utils/clubFeatures';
import { teamWordsOf } from 'utils/teamLabel';

// Helper function - outside component, no dependencies needed
const fetchImportErrors = async (api, importType, importJobId, page = 1) => {
  try {
    const response = await api.getImportErrors(importJobId, page, 50);
    return {
      errors: response.data.errors,
      pagination: response.data.pagination,
    };
  } catch (err) {
    console.error(`[${importType}] Fetch errors error:`, err);
    toast.error('Failed to load import errors');
    return { errors: [], pagination: null };
  }
};

const ImportPage = () => {
  const { selectedClub } = useSelector((state) => state.user || {});
  const location = useLocation();
  const navigate = useNavigate();
  const { meetId } = useParams();

  const importType = getImportTypeFromPath(location.pathname);
  // A club that groups by year level is told about the Year Level column and given its own template.
  // A club that has teams is told about the Team column, in its own word, and its template has it.
  const config = getImportConfig(
    importType,
    groupingBasisOf(selectedClub),
    clubFeaturesOf(selectedClub).teams ? teamWordsOf(selectedClub) : null
  );

  // The meet-scoped registration import is reached from the Meet Registrations page; offer a way back to it. The
  // member importer is club-scoped (no meet) and has no such parent page, so the button is hidden there.
  const isRegistrationImport =
    importType === IMPORT_TYPES.REGISTRATION_IMPORT.key && !!meetId;

  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [errorsPagination, setErrorsPagination] = useState(null);
  const [statusPolling, setStatusPolling] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [mergeMode, setMergeMode] = useState(true); // Default to merge mode for registration imports
  const [previewData, setPreviewData] = useState(null); // dry-run preview result (new resolution path)
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyResult, setApplyResult] = useState(null); // synchronous apply result (new resolution path)
  const [applyLoading, setApplyLoading] = useState(false);
  const [applySummaryOnly, setApplySummaryOnly] = useState(false); // true = persisted last-import summary (tiles only)
  const [lastImportFileName, setLastImportFileName] = useState(null); // member importer subtitle (no job to read it from)
  const [inviteOffer, setInviteOffer] = useState(null); // {count} — post-apply "send invitations?" dialog (live apply only)
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // BulkOperationResultModal shape, shown only when some sends failed

  const api = {
    importCsv: config.importCsv,
    getImportStatus: config.getImportStatus,
    getImportErrors: config.getImportErrors,
    cancelImport: config.cancelImport,
    clearImportHistory: config.clearImportHistory,
    getLatestImport: config.getLatestImport,
    previewCsv: config.previewCsv,
    applyCsv: config.applyCsv,
  };

  // Check for existing import on component mount
  const checkExistingImport = useCallback(async () => {
    try {
      if (!selectedClub?.clubID) return;

      const response = await config.getLatestImport(
        selectedClub.clubID,
        getApiValue(config.importType)
      );
      const data = response.data;

      // Member importer (synchronous): no live job + no percent bar — restore the persisted last-import summary
      // (category tiles only) if one exists. The per-row detail isn't stored.
      if (config.supportsApply) {
        if (data && data.originalFileName) {
          setLastImportFileName(data.originalFileName); // subtitle: name of the last imported file
        }
        if (data && data.resultSummaryJson) {
          try {
            const summary = JSON.parse(data.resultSummaryJson);
            setApplyResult({
              ...summary,
              fileAccepted: true,
              rows: [],
              appliedAt:
                data.timestamps?.completedAt || data.timestamps?.createdAt || null,
            });
            setApplySummaryOnly(true);
          } catch (e) {
            console.error(`[${importType}] Could not parse last-import summary:`, e);
          }
        }
        return;
      }

      // Legacy queued importer (meet): load the job + its progress/errors and resume polling if still running.
      if (data && data.importJobId) {
        setImportStatus(data);

        if (data.status === 'Queued' || data.status === 'Running') {
          setStatusPolling(true);
        }

        if (data.hasErrors) {
          const result = await fetchImportErrors(
            { getImportErrors: config.getImportErrors },
            importType,
            data.importJobId
          );
          setImportErrors(result.errors);
          setErrorsPagination(result.pagination);
        }
      }
    } catch (err) {
      console.error(`[${importType}] Check existing import error:`, err);
    } finally {
      setLoading(false);
    }
    // Only depend on truly reactive values that come from props/state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClub?.clubID, importType]);

  useEffect(() => {
    checkExistingImport();
  }, [checkExistingImport]);

  // Poll for status updates
  useEffect(() => {
    let interval;
    if (statusPolling && importStatus?.importJobId) {
      interval = setInterval(async () => {
        try {
          const response = await config.getImportStatus(
            importStatus.importJobId
          );
          setImportStatus(response.data);

          // Stop polling if import is complete or failed
          if (
            response.data.status === 'Completed' ||
            response.data.status === 'Failed' ||
            response.data.status === 'Cancelled'
          ) {
            setStatusPolling(false);

            // Fetch errors if there are any
            if (response.data.hasErrors) {
              const result = await fetchImportErrors(
                { getImportErrors: config.getImportErrors },
                importType,
                response.data.importJobId
              );
              setImportErrors(result.errors);
              setErrorsPagination(result.pagination);
            }
          }
        } catch (err) {
          console.error(`[${importType}] Status polling error:`, err);
          setStatusPolling(false);
        }
      }, 2000); // Poll every 2 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
    // Only depend on truly reactive values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusPolling, importStatus?.importJobId, importType]);

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setPreviewData(null); // a new file invalidates any prior preview
    setApplyResult(null); // ...and any prior apply result (or restored summary)
    setApplySummaryOnly(false);
  };

  // Dry-run preview (new platform-aware resolution) — read-only, no writes.
  const handlePreview = async () => {
    if (!selectedFile) {
      toast.error('Please select a CSV file to preview');
      return;
    }
    try {
      setPreviewLoading(true);
      setPreviewData(null);
      setApplyResult(null); // re-previewing supersedes a prior result (or restored summary)
      setApplySummaryOnly(false);
      // The meet importer is meet-scoped (previewCsv(meetId, ...)); the member importer is club-scoped.
      const response =
        importType === IMPORT_TYPES.REGISTRATION_IMPORT.key && meetId
          ? await api.previewCsv(meetId, selectedFile)
          : await api.previewCsv(selectedClub.clubID, selectedFile);
      setPreviewData(response.data);
    } catch (err) {
      console.error(`[${importType}] Preview error:`, err);
      toast.error(
        err.response?.data?.message || err.message || 'Failed to preview the file'
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  // Confirm & Apply (new platform-aware resolution) — synchronous WRITE. Re-resolves the file server-side and
  // executes the plan, returning a preview-shaped result the same component renders. PIN-gated (replaces the legacy
  // queued Start Import for the member importer). The admin can fix the CSV, re-upload and re-apply freely.
  const handleApplyWithPin = async () => {
    if (!selectedFile) {
      toast.error('Please select a CSV file to import');
      return;
    }
    try {
      setApplyLoading(true);
      const fileName = selectedFile.name;
      // The meet importer is meet-scoped (applyCsv(meetId, ...)); the member importer is club-scoped.
      const isMeet = importType === IMPORT_TYPES.REGISTRATION_IMPORT.key && meetId;
      const response = isMeet
        ? await api.applyCsv(meetId, selectedFile)
        : await api.applyCsv(selectedClub.clubID, selectedFile);
      const result = response.data;
      setApplyResult(result);
      setApplySummaryOnly(false); // fresh apply → full result with per-row detail
      setPreviewData(null); // the result supersedes the dry-run preview

      if (result.fileAccepted) {
        setLastImportFileName(fileName);
        const changed = isMeet
          ? (result.registeredCount || 0) +
            (result.visitorAddedCount || 0) +
            (result.visitorCreatedCount || 0) +
            (result.updatedCount || 0)
          : (result.addedNewCount || 0) +
            (result.addedExistingCount || 0) +
            (result.updatedCount || 0);
        const failed = result.failedCount || 0;
        toast.success(
          `Import applied: ${changed} change${changed === 1 ? '' : 's'}` +
            (failed > 0 ? `, ${failed} failed` : '')
        );
        // Offer to invite the uninvited active contacts in this importer's scope. Live post-apply
        // moment only — the persisted-summary reload path deliberately never shows this dialog.
        if ((result.uninvitedActiveCount || 0) > 0) {
          setInviteOffer({ count: result.uninvitedActiveCount });
        }
      } else {
        toast.error('File was not accepted — see the report below.');
      }
    } catch (err) {
      console.error(`[${importType}] Apply error:`, err);
      toast.error(
        err.response?.data?.message || err.message || 'Failed to apply the import'
      );
    } finally {
      setApplyLoading(false);
    }
  };

  // Batch-send invitations to every uninvited active contact in this importer's scope (members for the
  // club importer, visitor contacts for the meet importer — the meet is passed so its name and date
  // appear in the visitor email). Targets are re-derived server-side from invitation status, so a re-run
  // retries exactly the failures; failed recipients stay "Not invited" and keep their per-row Invite buttons.
  const handleSendInvitations = async () => {
    try {
      setInviteSending(true);
      const isMeet = importType === IMPORT_TYPES.REGISTRATION_IMPORT.key && meetId;
      const response = await sendClubInvitationsBulk(
        selectedClub.clubID,
        isMeet ? 'visitors' : 'members',
        isMeet ? meetId : undefined
      );
      const { sentCount = 0, failed = [] } = response.data || {};
      setInviteOffer(null);
      if (failed.length === 0) {
        toast.success(`${sentCount} invitation${sentCount === 1 ? '' : 's'} sent`);
      } else {
        setInviteResult({
          succeededCount: sentCount,
          skippedCount: failed.length,
          results: failed.map((f) => ({
            id: f.name ? `${f.name} (${f.email})` : f.email,
            success: false,
            reason: f.reason,
          })),
        });
      }
    } catch (err) {
      console.error(`[${importType}] Bulk invitation error:`, err);
      toast.error(
        err.response?.data || err.message || 'Failed to send invitations'
      );
    } finally {
      setInviteSending(false);
    }
  };

  // PIN-protected action handler
  const handleImportStartWithPin = async (data, pin) => {
    if (!selectedFile) {
      toast.error('Please select a CSV file to import');
      return;
    }

    try {
      setLoading(true);
      let response;

      if (importType === IMPORT_TYPES.REGISTRATION_IMPORT.key && meetId) {
        response = await api.importCsv(meetId, selectedFile, mergeMode ? 1 : 0); // pass the import mode (merge or replace)
      } else {
        response = await api.importCsv(selectedClub.clubID, selectedFile);
      }

      toast.success(
        `Import started successfully! ${response.data.totalRecords} records queued for processing`
      );

      // Immediately show the progress tile with initial data
      setImportStatus({
        importJobId: response.data.importJobId,
        status: 'Queued',
        progress: {
          totalRecords: response.data.totalRecords,
          processedRecords: 0,
          successCount: 0,
          updatedCount: 0,
          upToDateCount: 0,
          errorCount: 0,
          currentBatch: 1,
          totalBatches: 1,
          percentComplete: 0,
          queuePosition: response.data.queuePosition || 1,
        },
        timestamps: {
          createdAt: new Date().toISOString(),
          startedAt: null,
          completedAt: null,
          lastUpdated: new Date().toISOString(),
          duration: null,
        },
        completionMessage: response.data.message,
        failureReason: null,
        hasErrors: false,
        originalFileName: selectedFile.name,
        fileSizeBytes: selectedFile.size,
        importType: config.importType,
        initiatedByUserName: 'Current User', // You might want to get this from user state
      });

      setSelectedFile(null);
      setImportErrors([]);
      setErrorsPagination(null);
      setStatusPolling(true);
    } catch (err) {
      console.error(`[${importType}] Import start error:`, err);

      // Handle structured error response with message and errors array
      if (err.response?.data) {
        const errorData = err.response.data;
        let errorMessage = errorData.message || 'Failed to start import';

        // If there are specific errors, append them to the message
        if (
          errorData.errors &&
          Array.isArray(errorData.errors) &&
          errorData.errors.length > 0
        ) {
          errorMessage += '\n\nErrors:\n• ' + errorData.errors.join('\n• ');
        }

        toast.error(errorMessage);
      } else {
        toast.error(err.message || 'Failed to start import');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelImport = async () => {
    try {
      setLoading(true);
      await api.cancelImport(importStatus.importJobId);
      toast.success('Import cancelled successfully');
      setShowCancelConfirm(false);
      // Let the polling continue and handle the cancelled status naturally
    } catch (err) {
      console.error(`[${importType}] Cancel import error:`, err);
      toast.error(err.response?.data?.message || 'Failed to cancel import');
    } finally {
      setLoading(false);
    }
  };

  const handleClearHistory = async () => {
    try {
      setLoading(true);
      await api.clearImportHistory(
        selectedClub.clubID,
        getApiValue(config.importType)
      );
      toast.success('Import history cleared successfully');

      setImportStatus(null);
      setImportErrors([]);
      setErrorsPagination(null);
      setSelectedFile(null);
      setShowClearConfirm(false);
      setStatusPolling(false);
    } catch (err) {
      console.error(`[${importType}] Clear history error:`, err);
      toast.error(
        err.response?.data?.message || 'Failed to clear import history'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleErrorsPageChange = (page) => {
    if (importStatus?.importJobId) {
      fetchImportErrors(
        { getImportErrors: config.getImportErrors },
        importType,
        importStatus.importJobId,
        page
      ).then((result) => {
        setImportErrors(result.errors);
        setErrorsPagination(result.pagination);
      });
    }
  };

  const canStartNewImport =
    !importStatus ||
    (importStatus.status !== 'Queued' && importStatus.status !== 'Running');

  const canCancelImport =
    importStatus &&
    (importStatus.status === 'Queued' || importStatus.status === 'Running');

  const hasImportHistory = importStatus !== null;

  const formatTextWithBoldPatterns = (text, boldPatterns = []) => {
    if (!text || !boldPatterns || boldPatterns.length === 0) {
      return text;
    }

    const pattern = boldPatterns
      .map((pattern) => pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('|');

    const regex = new RegExp(`(${pattern})`, 'g');
    const parts = text.split(regex);

    return parts.map((part, index) => {
      const isBold = boldPatterns.some((pattern) => part === pattern);

      if (isBold) {
        let boldText = part;
        if (part.startsWith('[[') && part.endsWith(']]')) {
          boldText = part.slice(1, -1);
        } else if (part.startsWith('[') && part.endsWith(']')) {
          boldText = part.slice(1, -1);
        }
        return <strong key={index}>{boldText}</strong>;
      }

      return part;
    });
  };

  return (
    <>
      <MetaTags title={`SplitFast | ${config.title}`} />
      <MainContent loading={loading}>
        <PinProtectedAction clubId={selectedClub?.clubID}>
          {({ requestPinValidation }) => (
            <>
              <PageHeader
                title={config.title}
                subTitle={
                  importStatus
                    ? `Last import: ${importStatus.originalFileName}`
                    : lastImportFileName
                      ? `Last import: ${lastImportFileName}`
                      : 'No previous imports'
                }
              >
                <div className={styles.buttonContainer}>
                  {isRegistrationImport && (
                    <CustomNewButton
                      variant="outline"
                      icon={<ChevronLeftIcon />}
                      onClick={() =>
                        navigate(
                          routes.admin.meetGeneration.url(
                            selectedClub?.clubID,
                            meetId
                          )
                        )
                      }
                    >
                      Back to Registrations
                    </CustomNewButton>
                  )}
                  {canCancelImport && (
                    <CustomNewButton
                      label="Cancel Import"
                      variant="danger"
                      onClick={() => setShowCancelConfirm(true)}
                    />
                  )}
                  {/* Clear History is only for the legacy job-based importer (meet); the synchronous member importer
                      has no history to manage — its last-import summary is just refreshed on the next import. */}
                  {!config.supportsApply && hasImportHistory && (
                    <CustomNewButton
                      label="Clear History"
                      variant="info"
                      onClick={() => setShowClearConfirm(true)}
                    />
                  )}
                </div>
              </PageHeader>

              {/* Always show upload section, but disable during import */}
              <div className={styles.uploadSection}>
                <div className={styles.uploadHeader}>
                  <h3 className={styles.sectionTitle}>Upload a CSV File</h3>
                  <p className={styles.sectionDescription}>
                    {formatTextWithBoldPatterns(
                      config.fileDescription,
                      config.boldPatterns
                    )}
                  </p>
                  {config.templates && config.templates.length > 0 && (
                    <p className={styles.templateLinks}>
                      {config.templates.map((template, index) => (
                        <a
                          key={index}
                          href={template.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {template.name}
                        </a>
                      ))}
                    </p>
                  )}
                </div>

                {/* Merge/Replace Options */}
                {config.showMergeReplaceOptions && (
                  <div className={styles.importOptions}>
                    <h4 className={styles.optionsTitle}>Import Options</h4>
                    <div className={styles.radioGroup}>
                      <label className={styles.radioOption}>
                        <input
                          type="radio"
                          name="importMode"
                          checked={mergeMode}
                          onChange={() => setMergeMode(true)}
                          disabled={!canStartNewImport}
                        />
                        <span className={styles.radioLabel}>
                          <strong>Merge</strong> - Merge imported data with
                          existing data. Updates existing records and adds new
                          ones, but does not remove any existing data.
                        </span>
                      </label>
                      <label className={styles.radioOption}>
                        <input
                          type="radio"
                          name="importMode"
                          checked={!mergeMode}
                          onChange={() => setMergeMode(false)}
                          disabled={!canStartNewImport}
                        />
                        <span className={styles.radioLabel}>
                          <strong>Replace</strong> - Replace all existing data
                          with imported data. Removes all event registrations
                          that are not in the import file.
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                <FileUpload
                  onFileSelect={handleFileSelect}
                  acceptedTypes=".csv"
                  disabled={!canStartNewImport}
                />

                {selectedFile && canStartNewImport && (
                  <div className={styles.uploadActions}>
                    {config.supportsPreview && (
                      <CustomNewButton
                        label={previewLoading ? 'Previewing…' : 'Preview Changes'}
                        variant="info"
                        onClick={handlePreview}
                        disabled={!selectedFile || previewLoading || applyLoading}
                      />
                    )}
                    {config.supportsApply ? (
                      // Member importer: review-first — Confirm & Apply is enabled once a preview is in hand.
                      <CustomNewButton
                        label={applyLoading ? 'Applying…' : 'Confirm & Apply'}
                        variant="primary"
                        onClick={() =>
                          requestPinValidation(
                            'apply-import',
                            handleApplyWithPin,
                            null,
                            (() => {
                              const accepted = previewData && previewData.fileAccepted;
                              const isMeetImport =
                                importType === IMPORT_TYPES.REGISTRATION_IMPORT.key;
                              const changes = !accepted
                                ? 0
                                : isMeetImport
                                ? (previewData.registerCount || 0) +
                                  (previewData.addVisitorCount || 0) +
                                  (previewData.newVisitorCount || 0) +
                                  (previewData.updateCount || 0)
                                : (previewData.newCount || 0) +
                                  (previewData.addCount || 0) +
                                  (previewData.updateCount || 0);
                              const blocked = accepted ? previewData.blockCount || 0 : 0;
                              return (
                                `Applying this ${config.title.toLowerCase()} requires an admin PIN code. ` +
                                `${changes} change${changes === 1 ? '' : 's'} will be written` +
                                (blocked > 0
                                  ? `; ${blocked} blocked row${blocked === 1 ? '' : 's'} will be skipped.`
                                  : '.')
                              );
                            })()
                          )
                        }
                        disabled={
                          !previewData ||
                          !previewData.fileAccepted ||
                          previewLoading ||
                          applyLoading
                        }
                      />
                    ) : (
                      <CustomNewButton
                        label="Start Import"
                        variant="primary"
                        onClick={() =>
                          requestPinValidation(
                            'start-import',
                            handleImportStartWithPin,
                            null,
                            `Starting a ${config.title.toLowerCase()} requires an admin PIN code. This will process and import the selected CSV file. ${mergeMode ? 'Data will be merged with existing records.' : 'Existing data will be replaced with imported data.'}`
                          )
                        }
                        disabled={!selectedFile}
                      />
                    )}
                  </div>
                )}
                {config.supportsApply && selectedFile && canStartNewImport && !previewData && !applyResult && (
                  <p className={styles.applyHint}>
                    Preview the file to review what will change, then Confirm &amp; Apply.
                  </p>
                )}
              </div>

              {config.supportsPreview && previewData && !applyResult && (
                <ImportPreview
                  data={previewData}
                  mode="preview"
                  variant={
                    importType === IMPORT_TYPES.REGISTRATION_IMPORT.key
                      ? 'meet'
                      : 'member'
                  }
                />
              )}

              {applyResult && (
                <ImportPreview
                  data={applyResult}
                  mode="result"
                  summaryOnly={applySummaryOnly}
                  variant={
                    importType === IMPORT_TYPES.REGISTRATION_IMPORT.key
                      ? 'meet'
                      : 'member'
                  }
                />
              )}

              {/* Legacy job-based progress/errors — only for importers still using the queued path (meet).
                  The member importer is synchronous and reports via the result component above. */}
              {!config.supportsApply && importStatus && (
                <ImportProgress importStatus={importStatus} />
              )}

              {!config.supportsApply && importErrors.length > 0 && (
                <ImportErrors
                  errors={importErrors}
                  pagination={errorsPagination}
                  onPageChange={handleErrorsPageChange}
                />
              )}

              {!canStartNewImport && !importStatus && (
                <div className={styles.noImportMessage}>
                  <p>No import in progress. Upload a CSV file to begin.</p>
                </div>
              )}
            </>
          )}
        </PinProtectedAction>
      </MainContent>

      {/* Cancel Import Confirmation Modal */}
      <CustomNewConfirm
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        title="Cancel Import"
        onConfirm={handleCancelImport}
        confirmButtonText="Cancel Import"
        confirmButtonVariant="danger"
      >
        <div className={styles.modalDescription}>
          Are you sure you want to cancel the current import?
        </div>
        <div className={styles.dangerText}>
          ⚠️ This action cannot be undone. Any records that have already been
          processed will remain in the system.
        </div>
      </CustomNewConfirm>

      {/* Clear History Confirmation Modal */}
      <CustomNewConfirm
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        title="Clear Import History"
        onConfirm={handleClearHistory}
        confirmButtonText="Clear History"
        confirmButtonVariant="danger"
      >
        <div className={styles.modalDescription}>
          Are you sure you want to clear the import history?
        </div>
        <div className={styles.dangerText}>
          ⚠️ This will erase the history and error messages for this import. No
          imported records will be deleted.
        </div>
      </CustomNewConfirm>

      {/* Post-import invitation offer — appears once, right after a live apply (never on the
          persisted-summary reload path). Declining loses nothing: every uninvited contact keeps
          their per-row Invite button on the Members / Visitors pages. */}
      <CustomNewConfirm
        isOpen={!!inviteOffer}
        onClose={() => setInviteOffer(null)}
        title="Send portal invitations?"
        onConfirm={handleSendInvitations}
        confirmButtonText={
          inviteSending
            ? 'Sending…'
            : `Send ${inviteOffer?.count || 0} invitation${(inviteOffer?.count || 0) === 1 ? '' : 's'}`
        }
        confirmButtonVariant="primary2"
        cancelButtonText="Not now"
        isLoading={inviteSending}
      >
        <div className={styles.modalDescription}>
          {isRegistrationImport
            ? `${inviteOffer?.count || 0} visitor contact${(inviteOffer?.count || 0) === 1 ? ' has' : 's have'} not been invited to the SplitFast portal yet. Would you like to send them invitation emails now?`
            : `${inviteOffer?.count || 0} active member${(inviteOffer?.count || 0) === 1 ? ' has' : 's have'} not been invited to the SplitFast portal yet. Would you like to send them invitation emails now?`}
        </div>
        <div className={styles.modalDescription}>
          {isRegistrationImport
            ? 'If you skip this, you can still send invitations to individual visitors at any time from the Visitors page.'
            : 'If you skip this, you can still send invitations to individual members at any time from the Members page.'}
        </div>
      </CustomNewConfirm>

      {/* Per-recipient outcome, shown only when some invitation emails failed to send. Failed
          recipients remain "Not invited" so they can be retried individually or via re-import. */}
      <BulkOperationResultModal
        show={!!inviteResult}
        title="Invitation Results"
        result={inviteResult}
        itemLabel="invitation"
        onClose={() => setInviteResult(null)}
      />
    </>
  );
};

export default ImportPage;
